import { useState, useRef } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Expand, FilePenLine, X, SquareArrowUpRight, Minimize2 } from 'lucide-react';
import { Tabs, TabsContent, TabsTrigger } from '@/components/ui/tabs';
import { ScrollableTabsList } from '@/components/ScrollableTabsList';
import ClientDetailsContent from '@pages/clients/clientComponents/ClientDetailsContent';
import DataExchangeContent from '@pages/clients/clientComponents/DataExchangeContent';
import ContactListContent from '@pages/clients/clientComponents/ContactListContent';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ClientContactSchema } from './client.schema';
import { z } from 'zod';
import { useBillingPreferenceGet, useClientClientIdGet } from '@brainvaulttech/dgr-react-sdk';
import { useUserStore } from '@/stores/user';
import Loader from '@/components/Loader';
import { hasFeature } from '@/services/featureService';
import { hasBillingPreferenceEditAccess } from './BillingPreferencesUpdate';
import { isAdminOrSupervisor } from '@/services/userService';
import { Feature } from '@/utils/constant';
import { hasRoleAccess, isAdminLikeRole } from '@/lib/roles';

const tabs = [
  { value: 'Client Details', label: 'Client Details', roles: ['ADMIN', 'OPERATOR', 'ACCOUNTANT'] },
  { value: 'Contact List', label: 'Contact List', roles: ['ADMIN', 'OPERATOR'] },
  { value: 'Data Exchange', label: 'Data Exchange', roles: ['ADMIN', 'OPERATOR', 'ACCOUNTANT'] },
];

export const ClientDetails = ({
  open,
  onCloseClick,
  role,
  clientId,
}: {
  open: boolean;
  onCloseClick: () => void;
  clientId: string;
  role: string;
}) => {
  const user = useUserStore((state) => state.user);
  const navigate = useNavigate();

  const [fullScreen, setFullScreen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('Client Details');
  const sheetContentRef = useRef<HTMLDivElement>(null);

  const { data: clientData, isFetching, isError } = useClientClientIdGet(clientId);
  const { data: billingPreferencesData, isFetching: isBillingFetching } = useBillingPreferenceGet(clientId);

  const visibleTabs = tabs.filter((tab) => hasRoleAccess(role, tab.roles));
  const tabWidth = visibleTabs.length <= 3 ? `${100 / visibleTabs.length}%` : '33%';

  const isAdmin = isAdminLikeRole(user?.roleId);
  const canViewBillingPreferences = isAdminOrSupervisor(user);
  const canEditBillingPreference =
    hasFeature(Feature.CLIENT_BILLING_PREFERENCES) && hasBillingPreferenceEditAccess(user);

  const renderDropdownItems = () => {
    switch (activeTab) {
      case 'Client Details':
        return (
          <>
            {isAdmin && (
              <>
                <DropdownMenuItem onClick={() => navigate(`/clients/update/${clientId}?stepper=Client+Information`)}>
                  <div className="flex justify-between items-center gap-2 w-full text-[12px] text-primary font-[500]">
                    <span>{`Edit Client's Details`}</span>
                    <SquareArrowUpRight className="h-4 w-4" color="#0A2240" />
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="h-[2px]" />
                <DropdownMenuItem
                  className="cursor-pointer hover:bg-accent"
                  onClick={() => navigate(`/clients/update/${clientId}?stepper=Notes+%2F+Instructions`)}
                >
                  <div className="flex justify-between items-center gap-2 w-full text-[12px] text-primary font-[500]">
                    <span>{`Edit Client's Instructions`}</span>
                    <SquareArrowUpRight className="h-4 w-4" color="#0A2240" />
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="h-[2px]" />
              </>
            )}
            {canEditBillingPreference && (
              <>
                <DropdownMenuItem
                  className="cursor-pointer hover:bg-accent"
                  onClick={() => navigate(`/clients/update/${clientId}/billing-preference`)}
                >
                  <div className="flex justify-between items-center gap-2 w-full text-[12px] text-primary font-[500]">
                    <span>Edit Billing Preferences </span>
                    <SquareArrowUpRight className="h-4 w-4" color="#0A2240" />
                  </div>
                </DropdownMenuItem>
              </>
            )}
          </>
        );
      case 'Contact List':
        return isAdmin ? (
          <DropdownMenuItem onClick={() => navigate(`/clients/update/${clientId}?stepper=Employees%2F+Contact`)}>
            <div className="flex justify-between items-center gap-2 w-full text-[12px] text-primary font-[500]">
              <span>Edit Contact List</span>
              <SquareArrowUpRight className="h-4 w-4" color="#0A2240" />
            </div>
          </DropdownMenuItem>
        ) : null;
      case 'Data Exchange':
        return isAdmin ? (
          <DropdownMenuItem onClick={() => navigate(`/clients/update/${clientId}?stepper=Data+Exchange+Contacts`)}>
            <div className="flex justify-between items-center gap-2 w-full text-[12px] text-primary font-[500]">
              <span>Edit Data Exchange</span>
              <SquareArrowUpRight className="h-4 w-4" color="#0A2240" />
            </div>
          </DropdownMenuItem>
        ) : null;
      default:
        return null;
    }
  };

  return (
    <Sheet modal={fullScreen} open={open}>
      <SheetContent
        ref={sheetContentRef}
        hideCloseButton={true}
        className="w-[80vw] md:w-[500px] scrollbar-hide bg-white"
        side={fullScreen ? 'center' : 'right'}
        tabIndex={-1}
      >
        <Loader isLoading={isFetching || isBillingFetching}>
          {isError ? (
            <div className="flex h-full items-center relative justify-center w-full text-placeholder">
              <div className="border-[1px] p-[6px] absolute top-6 right-6 rounded-md" onClick={onCloseClick}>
                <X color="#676767" className="h-[20px] w-[20px] cursor-pointer" />
              </div>
              <span>No Results</span>
            </div>
          ) : (
            <Tabs
              className="w-full text-black"
              defaultValue={visibleTabs?.[0]?.value}
              onValueChange={(value) => {
                setActiveTab(value);
              }}
            >
              <SheetHeader className="flex flex-col gap-4 p-6 pb-0 sticky top-0 bg-white">
                <SheetTitle className={`flex flex-col gap-4 bg-white  pb-0`}>
                  <div className="flex flex-row gap-2 justify-between align-middle">
                    <div className="flex flex-row gap-2">
                      <div className="bg-[#9747FF33] rounded-md text-normal font-semibold p-2 h-[48px] min-w-[48px] flex justify-center text-[#55288F]">
                        <span>
                          {clientData?.data.type === 'INDIVIDUAL'
                            ? `${clientData.data.firstName?.[0]}${clientData.data.lastName?.[0]}`
                            : clientData?.data.clientName?.[0]}
                        </span>
                      </div>

                      <div
                        className="flex flex-col"
                        style={{
                          overflowWrap: 'anywhere',
                        }}
                      >
                        <span className="text-base font-medium">{clientData?.data.clientName}</span>
                        <span className="text-sm font-normal">#{clientData?.data?.code}</span>
                      </div>
                    </div>
                    <div className="flex flex-row gap-2 h-[32px]">
                      <div
                        className="border-[1px] p-[6px] rounded-md justify-center"
                        onClick={() => setFullScreen(!fullScreen)}
                      >
                        {fullScreen ? (
                          <Minimize2 color="#676767" className="h-[20px] w-[20px] cursor-pointer" />
                        ) : (
                          <Expand color="#676767" className="h-[20px] w-[20px] cursor-pointer" />
                        )}
                      </div>
                      {(isAdmin || (activeTab === 'Client Details' && canEditBillingPreference)) && (
                        <div className="border-[1px] p-[6px] rounded-md">
                          <DropdownMenu>
                            <DropdownMenuTrigger>
                              <FilePenLine color="#676767" className="h-[20px] w-[20px] cursor-pointer" />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className={`w-fit mr-[60px] mt-3 ${fullScreen ? 'mr-[60px]' : ''}`}>
                              {renderDropdownItems()}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      )}
                      <div className="border-[1px] p-[6px] rounded-md" onClick={onCloseClick}>
                        <X color="#676767" className="h-[20px] w-[20px] cursor-pointer" />
                      </div>
                    </div>
                  </div>
                  <div className="text-[#969696] font-[400] flex flex-col text-sm">
                    <span
                      style={{
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {[clientData?.data.address1, clientData?.data.address2].filter(Boolean).join(', ')}
                    </span>
                    <span>
                      {[clientData?.data.city, clientData?.data.state].filter(Boolean).join(', ') +
                        ' ' +
                        clientData?.data.zipCode}
                    </span>
                  </div>
                </SheetTitle>
                <ScrollableTabsList className="!mt-0">
                  {visibleTabs.map((tab) => (
                    <TabsTrigger
                      key={tab.value}
                      value={tab.value}
                      style={{ width: tabWidth }}
                      autoFocus={tab.value === 'Client Details'}
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </ScrollableTabsList>
              </SheetHeader>
              <div className={`w-full !mt-0 px- pb-2 text-[#0A0A0A]`}>
                <TabsContent value="Client Details" className="mt-0 h-full overflow-y-scroll scrollbar-hide">
                  <ClientDetailsContent
                    clientData={clientData?.data}
                    role={role}
                    billingPreferencesData={billingPreferencesData?.data}
                    canViewBillingPreferences={canViewBillingPreferences}
                  />
                </TabsContent>
                <TabsContent value="Contact List" className="mt-0 overflow-y-scroll scrollbar-hide pb-10">
                  <ContactListContent
                    fullScreen={fullScreen}
                    clientId={clientId}
                    contacts={clientData?.data?.contacts as z.infer<typeof ClientContactSchema>['contacts'] | undefined}
                  />
                </TabsContent>
                <TabsContent value="Data Exchange" className="mt-0 overflow-y-scroll scrollbar-hide ">
                  <DataExchangeContent clientData={clientData?.data} />
                </TabsContent>
              </div>
            </Tabs>
          )}
        </Loader>
      </SheetContent>
    </Sheet>
  );
};
